import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    FilingEvidenceAttempt, evidenceOutcomeLabel, evidenceOutcomeStyle, getFilingEvidence,
} from '../../helper/filingEvidence';

/**
 * Filing evidence viewer — the exact payload sent to a state and the state's own reply, per attempt.
 *
 * WHY IT EXISTS. Evidence has been captured on every submission since 2026-07-28 and nothing could read
 * it. When SLIP+ accepted a real TN batch, the acceptance — and the per-policy confirmation numbers a
 * regulator would be quoted — were visible only on SLIP+'s own website. An audit asks what we sent, not
 * only what we believe happened, so this exists to answer that from inside the app.
 *
 * IT IS A TIMELINE, NOT A SNAPSHOT. A transaction normally has more than one attempt: the submit writes
 * `queued`, and reconciliation later appends the state's real answer against the same submission
 * number. Rendered oldest-first, because the sequence is the story.
 *
 * AN ATTEMPT IS HISTORY AND STAYS HISTORY. `queued` still reads "awaiting the state" here even after a
 * later attempt records acceptance — rewriting it to match the current status would defeat the point of
 * an append-only trail.
 */

interface Props {
    open: boolean;
    onClose: () => void;
    customerPolicyId: number;
    /** For the title only — a filer thinks in policy numbers, not ids. */
    policyNumber?: string | null;
    insuredName?: string | null;
    token?: string;
}

const MONO = '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace';

function fmt(ts: string | null): string {
    if (!ts) return '';
    const d = new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function bytes(n: number | null): string {
    if (n == null) return '';
    if (n < 1024) return `${n} B`;
    return `${(n / 1024).toFixed(1)} KB`;
}

/** One collapsible document. Copy targets the FULL rendered text, not the visible slice. */
function BodyBlock({ label, body, truncated, sentBytes }: {
    label: string; body: string | null; truncated: boolean; sentBytes: number | null;
}) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        if (!body) return;
        try { await navigator.clipboard.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* clipboard blocked — the text is selectable anyway */ }
    };

    // NULL is not the same as empty. A queued async filing genuinely has no reply yet; rendering an
    // empty document would assert the state replied with nothing, which is a different claim.
    if (body == null) {
        return (
            <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#5b6770' }}>{label}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                    Not recorded — an asynchronous filing has no reply at the moment it is submitted. The
                    state's answer is appended as a separate attempt once it arrives.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => setOpen((o) => !o)}
                    startIcon={open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '12px', color: '#2099d2', p: 0, minWidth: 0 }}>
                    {label}
                </Button>
                <Typography sx={{ fontSize: '11px', color: '#5b6770' }}>{bytes(sentBytes)}</Typography>
                {truncated && (
                    <Tooltip title="Shown in full it would be too large for this dialog. Only part of the document is displayed — the byte count above is what was actually sent.">
                        <Chip size="small" label="TRUNCATED" sx={{ backgroundColor: '#fff4e5', color: '#a15c07', fontWeight: 700, height: 18, fontSize: '10px' }} />
                    </Tooltip>
                )}
                {open && (
                    <Button size="small" onClick={copy} startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '11px', color: '#475569' }}>
                        {copied ? 'Copied' : 'Copy'}
                    </Button>
                )}
            </Box>
            {open && (
                <Box component="pre" sx={{
                    mt: 0.5, mb: 0, p: 1, maxHeight: 300, overflow: 'auto',
                    backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px',
                    fontFamily: MONO, fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                    {body}
                </Box>
            )}
        </Box>
    );
}

function AttemptCard({ a, index }: { a: FilingEvidenceAttempt; index: number }) {
    const style = evidenceOutcomeStyle(a.outcome);
    return (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', p: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>#{index + 1}</Typography>
                <Chip size="small" label={evidenceOutcomeLabel(a.outcome)}
                    sx={{ backgroundColor: style.bg, color: style.fg, fontWeight: 700 }} />
                {a.filingSystem && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#5b6770' }}>
                        {a.filingSystem}{a.state ? ` · ${a.state}` : ''}
                    </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Typography sx={{ fontSize: '11px', color: '#5b6770' }}>
                    {fmt(a.submittedAt || a.createdAt)}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.75 }}>
                {a.submissionNumber && (
                    <Typography sx={{ fontSize: '12px', color: '#334155' }}>
                        Submission <b>{a.submissionNumber}</b>
                    </Typography>
                )}
                {a.submissionStatus && (
                    <Typography sx={{ fontSize: '12px', color: '#334155' }}>
                        State status: <b>{a.submissionStatus}</b>
                    </Typography>
                )}
                {a.transactionType && (
                    <Typography sx={{ fontSize: '12px', color: '#5b6770' }}>
                        Transaction {a.transactionType}{a.transEffDate ? ` · eff ${a.transEffDate}` : ''}
                    </Typography>
                )}
            </Box>

            {/* The state's own words about why it went this way. Only failures carry one. */}
            {a.detail && (
                <Typography sx={{ fontSize: '12.5px', color: style.fg, fontWeight: 600, mt: 0.75 }}>
                    {a.detail}
                </Typography>
            )}

            {a.requestFileName && (
                <Typography sx={{ fontSize: '11px', color: '#5b6770', mt: 0.5, fontFamily: MONO }}>
                    {a.requestFileName}
                </Typography>
            )}

            <BodyBlock label="What we sent" body={a.requestBody} truncated={a.requestTruncated} sentBytes={a.requestBytes} />
            <BodyBlock label="What the state said" body={a.responseBody} truncated={a.responseTruncated} sentBytes={a.responseBytes} />
        </Box>
    );
}

export function FilingEvidenceDialog({ open, onClose, customerPolicyId, policyNumber, insuredName, token }: Props) {
    const [loading, setLoading] = useState(false);
    const [attempts, setAttempts] = useState<FilingEvidenceAttempt[]>([]);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await getFilingEvidence(token, customerPolicyId);
            if (!res.ok) {
                setAttempts([]);
                setError(res.unavailable
                    ? 'Filing evidence is not available on this environment yet.'
                    : res.forbidden
                        ? 'You do not have access to filing evidence.'
                        : (res.message || 'Filing evidence could not be loaded.'));
                return;
            }
            setAttempts(res.attempts);
        } catch (e: any) {
            setAttempts([]);
            setError(e?.message || 'Filing evidence could not be loaded.');
        } finally {
            setLoading(false);
        }
    }, [token, customerPolicyId]);

    // Fetch only while OPEN, and refetch on reopen so a dialog reopened after a filing is not stale.
    useEffect(() => { if (open) load(); }, [open, load]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '17px', pb: 0.5 }}>
                Filing history
                <Typography sx={{ fontSize: '13px', color: '#5b6770', fontWeight: 500 }}>
                    {insuredName || ''}{insuredName && policyNumber ? ' · ' : ''}{policyNumber || ''}
                </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                        <CircularProgress size={18} /><Typography sx={{ fontSize: '13px' }}>Loading…</Typography>
                    </Box>
                )}
                {!loading && error && (
                    <Typography sx={{ fontSize: '13px', color: '#c62828' }}>{error}</Typography>
                )}
                {!loading && !error && attempts.length === 0 && (
                    <Typography sx={{ fontSize: '13px', color: '#5b6770' }}>
                        This policy has never been submitted to a state. Once it is filed, every attempt —
                        what was sent and what came back — is recorded here.
                    </Typography>
                )}
                {!loading && !error && attempts.length > 0 && (
                    <>
                        <Typography sx={{ fontSize: '12px', color: '#5b6770', mb: 1 }}>
                            {attempts.length} attempt{attempts.length === 1 ? '' : 's'}, oldest first. Each
                            row is what happened at that moment and is never rewritten.
                        </Typography>
                        {attempts.map((a, i) => <AttemptCard key={a.evidenceId} a={a} index={i} />)}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default FilingEvidenceDialog;
